import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import AutoComplete from '../components/AutoComplete';
import { sucursalService, ubigeoService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/datatable.css';

const Sucursales = () => {
  const [records, setRecords] = useState([]);
  const [ubigeos, setUbigeos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const emptyForm = { codigo: '', nombre: '', direccion: '', ubigeo: '' };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [suc, ubi] = await Promise.all([sucursalService.getAll(), ubigeoService.getAll()]);
      setRecords(suc.data);
      setUbigeos(ubi.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar sucursales.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    window._dtImportHandler = async (rows) => {
      let ok = 0, fail = 0;
      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const codigo = row['Codigo'] || row['codigo'] || '';
        const nombre = row['Nombre'] || row['nombre'] || '';
        let errorMsg = null;
        if (!codigo || !nombre) {
          fail++;
          errorMsg = `Fila ${i + 1}: ${!codigo ? 'Codigo' : 'Nombre'} vacio (obligatorio)`;
        } else {
          try {
            await sucursalService.create({
              codigo: String(codigo),
              nombre: String(nombre),
              direccion: row['Direccion'] || row['direccion'] || null,
              ubigeo: row['Ubigeo'] || row['ubigeo'] ? String(row['Ubigeo'] || row['ubigeo']) : null,
            });
            ok++;
          } catch (err) {
            fail++;
            errorMsg = `Fila ${i + 1} ("${nombre}"): ${getErrorDetail(err, 'Error al crear sucursal')}`;
          }
        }
        if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
      }
      fetchData();
    };
    return () => { window._dtImportHandler = null; };
  }, [fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setShowModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ codigo: r.codigo || '', nombre: r.nombre || '', direccion: r.direccion || '', ubigeo: r.ubigeo || '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.codigo || !form.nombre) { setAlert({ type: 'warning', message: 'Codigo y nombre son obligatorios.' }); return; }
    const data = {};
    Object.entries(form).forEach(([k, v]) => { data[k] = v === '' ? null : v; });
    try {
      if (editing) await sucursalService.update(editing.id_sucursal, data);
      else await sucursalService.create(data);
      setShowModal(false); fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Sucursal', message: `¿Eliminar "${r.nombre}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try { await sucursalService.delete(r.id_sucursal); setToast('Sucursal eliminada'); fetchData(); }
        catch { setAlert({ type: 'error', message: 'Error al eliminar.' }); }
      },
    });
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const columns = [
    { key: 'codigo', label: 'Codigo', width: 100, style: { fontWeight: 600 } },
    { key: 'nombre', label: 'Nombre' },
    { key: 'direccion', label: 'Direccion' },
    { key: 'ubigeo', label: 'Ubigeo', width: 100 },
  ];

  const importCols = [
    { key: 'Codigo', label: 'Codigo', required: true, example: 'SUC01', description: 'Codigo unico de la sucursal' },
    { key: 'Nombre', label: 'Nombre', required: true, example: 'Sucursal Lima Centro', description: 'Nombre de la sucursal' },
    { key: 'Direccion', label: 'Direccion', required: false, example: 'Av. Abancay 123, Lima', description: 'Direccion de la sucursal' },
    { key: 'Ubigeo', label: 'Ubigeo', required: false, example: '150101', description: 'Codigo de ubigeo INEI' },
  ];

  return (
    <Layout title="Sucursales" subtitle="Utilitarios">
      <div className="page-header">
        <h2>Sucursales</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nueva</button>
      </div>

      <DataTable columns={columns} data={records} idField="id_sucursal" onEdit={openEdit} onDelete={handleDelete}
        loading={loading} emptyMessage="No hay sucursales registradas" exportFileName="sucursales" importColumns={importCols} />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <h2>{editing ? 'Editar Sucursal' : 'Sucursal'}</h2>
                {!editing && <span className="modal-header-badge"><svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg></span>}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div style={{display:'grid',gridTemplateColumns:'140px 1fr',gap:'0 16px'}}>
                  <div className="form-row"><label>Codigo *</label><input name="codigo" value={form.codigo} onChange={handleChange} placeholder="Ej: SUC01" autoFocus /></div>
                  <div className="form-row"><label>Nombre *</label><input name="nombre" value={form.nombre} onChange={handleChange} placeholder="Nombre de la sucursal" /></div>
                </div>
                <div className="form-row"><label>Direccion</label><input name="direccion" value={form.direccion} onChange={handleChange} placeholder="Direccion" /></div>
                <AutoComplete
                  label="Ubigeo"
                  value={form.ubigeo}
                  options={ubigeos}
                  idField="codigo"
                  displayField="descripcion"
                  onChange={(val) => setForm(f => ({ ...f, ubigeo: val }))}
                  placeholder="Buscar departamento / provincia / distrito..."
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </Layout>
  );
};

export default Sucursales;
